import { formatWithTimezoneOffset } from "../utils/helpers";
import {
  Status,
  FetalSexCodeResultSerializer,
  RequisitionModelSerializer,
  RequisitionRow,
  DetailedRequisitionSerializer,
  PatientReleaseStatus,
  NewRequisitionForm,
} from "./types";
import {
  getCarrierPanelDisplayLabel,
  getTestOrderTypeFlags,
} from "../utils/testOrderDisplay";

export const GRID_REPORT_ID_PLACEHOLDER = "Report ID";

const getReportResultLabel = (
  status: Status,
  summary?:
    | "Low Risk"
    | "Negative"
    | "See Report"
    | "Test Not Performed"
    | null,
): string => {
  if (status === "Not Ordered" || status === "Processing") {
    return status;
  }
  return summary || "";
};

export const getRowUnread = (
  carrierStatus: Status,
  aneuploidyStatus: Status,
  carrierRead: boolean,
  aneuploidyRead: boolean,
): boolean => {
  const validAneuploidy =
    aneuploidyStatus === "Final" || aneuploidyStatus === "Corrected";
  const validCarrier =
    carrierStatus === "Final" || carrierStatus === "Corrected";

  if (validCarrier && !carrierRead) {
    return true;
  }
  if (validAneuploidy && !aneuploidyRead) {
    return true;
  }
  return false;
};

export const getRowFetalSex = (
  fetalSexInfo?: FetalSexCodeResultSerializer,
): string => {
  if (
    fetalSexInfo?.value === "Processing" ||
    fetalSexInfo?.status === "Processing"
  ) {
    return "Processing";
  }
  if (
    !fetalSexInfo ||
    !fetalSexInfo?.value ||
    fetalSexInfo?.status === "Not Ordered" ||
    fetalSexInfo?.status === "Request"
  ) {
    return "Not Ordered";
  }
  if (fetalSexInfo?.value === "NOT ORDERED") {
    return "Opted Out";
  }
  if (fetalSexInfo?.value === "MALE") {
    return "M";
  }
  if (fetalSexInfo?.value === "FEMALE") {
    return "F";
  }
  if (fetalSexInfo?.value === "MALE & MALE") {
    return "MM";
  }
  if (fetalSexInfo?.value === "FEMALE & FEMALE") {
    return "FF";
  }
  if (fetalSexInfo?.value === "MALE & FEMALE") {
    return "MF";
  }
  if (fetalSexInfo?.value === "ONE OR BOTH MALES") {
    return "See Report";
  }
  return "See Report";
};

const hasCfcResults = (
  cfcReport?: RequisitionModelSerializer["cfc_report"],
): boolean =>
  Boolean(
    cfcReport?.report_pk &&
    cfcReport.status !== "Processing" &&
    cfcReport.status !== "Not Ordered",
  );

const getCfcAneuploidyColumnLabel = (
  cfcReport?: RequisitionModelSerializer["cfc_report"],
): string => {
  if (cfcReport?.summary === "Test Not Performed") {
    return cfcReport.summary;
  }
  return "See Report";
};

export const getGestation = (
  gestation?:
    | "not_applicable"
    | "unknown"
    | "singleton"
    | "twin"
    | "triplet_or_more",
): string => {
  switch (gestation) {
    case "not_applicable":
      return "N/A";
    case "singleton":
      return "Singleton";
    case "twin":
      return "Twin";
    case "triplet_or_more":
      return "Triple Or More";
    default:
      return "";
  }
};

export const getEggDonor = (
  eggDonor?: "not_applicable" | "unknown" | "not_egg_donor" | "egg_donor",
): string => {
  switch (eggDonor) {
    case "not_applicable":
      return "N/A";
    case "not_egg_donor":
      return "Not Egg Donor";
    case "egg_donor":
      return "Egg Donor";
    default:
      return "";
  }
};

export const convertRequisitionToRequisitionRow = (
  requisition: RequisitionModelSerializer,
  index: number,
  useProviderCfcResults = false,
): RequisitionRow => {
  const useCfcResultInAneuploidyColumn =
    useProviderCfcResults && hasCfcResults(requisition.cfc_report);
  const aneuploidyResult = requisition.aneuploidy_report
    ? getReportResultLabel(
        requisition.aneuploidy_report.status,
        requisition.aneuploidy_report.summary,
      )
    : "Not Ordered";
  const carrier = requisition.unity_report
    ? getReportResultLabel(
        requisition.unity_report.status,
        requisition.unity_report.summary,
      )
    : "Not Ordered";
  const carrierPanelGeneCount = requisition.carrier_screen_panel_gene_count;
  const { isCarrierScreenOrder } = getTestOrderTypeFlags(
    carrier,
    aneuploidyResult,
    carrierPanelGeneCount,
  );

  return {
    index,
    sfID: requisition.sfid || "",
    reqID: requisition.req_identifier || "",
    name:
      `${requisition.first_name} ${requisition.last_name}` || "Patient Name",
    medicalRecordNumber: requisition.medical_record_number || "",
    dob: requisition.date_of_birth || "",
    physician: requisition.physician_name || "Physician Name",
    collected: formatWithTimezoneOffset(requisition.collection_date),
    reported: requisition.latest_report_date || "", // This date can't be transformed since its string searchable on the backend
    carrier,
    carrierSecondaryLabel: isCarrierScreenOrder
      ? getCarrierPanelDisplayLabel(carrierPanelGeneCount)
      : undefined,
    carrierReportID:
      requisition.unity_report?.report_pk || GRID_REPORT_ID_PLACEHOLDER,
    carrierRead: requisition.unity_report?.is_read ?? true,
    carrierPdfUrlsByLanguage: requisition.unity_report?.pdf_urls_by_language,
    carrierReleaseStatus:
      requisition.unity_report?.patient_release_state || "unreleased",
    carrierLims: requisition.unity_report?.is_lims ?? true,
    aneuploidy: useCfcResultInAneuploidyColumn
      ? getCfcAneuploidyColumnLabel(requisition.cfc_report)
      : aneuploidyResult,
    aneuploidyReportID:
      requisition.aneuploidy_report?.report_pk || GRID_REPORT_ID_PLACEHOLDER,
    aneuploidyReportType: useCfcResultInAneuploidyColumn ? "cfc" : "aneuploidy",
    aneuploidySecondaryLabel: useCfcResultInAneuploidyColumn
      ? "Unity Confirm™"
      : undefined,
    aneuploidyRead: useCfcResultInAneuploidyColumn
      ? (requisition.cfc_report.is_read ?? true)
      : (requisition.aneuploidy_report?.is_read ?? true),
    aneuploidyPdfUrlsByLanguage: useCfcResultInAneuploidyColumn
      ? requisition.cfc_report.pdf_urls_by_language
      : requisition.aneuploidy_report?.pdf_urls_by_language,
    del22q: requisition.aneuploidy_report?.del22q,
    aneuploidyReleaseStatus:
      requisition.aneuploidy_report?.patient_release_state || "unreleased",
    fetalReleaseStatus:
      requisition.aneuploidy_report?.fetal_sex?.status || "Final",
    rhdReleaseStatus:
      requisition.aneuploidy_report?.fetal_rhd?.status || "Final",
    faReleaseStatus:
      requisition.aneuploidy_report?.fetal_antigen_status || "Final",
    aneuploidyLims: useCfcResultInAneuploidyColumn
      ? (requisition.cfc_report.is_lims ?? false)
      : (requisition.aneuploidy_report?.is_lims ?? true),
    RhD: requisition.aneuploidy_report
      ? requisition.aneuploidy_report.fetal_rhd_fa
      : "Not Ordered",
    fetalSex: getRowFetalSex(requisition.aneuploidy_report?.fetal_sex),
    missingInfo:
      requisition.is_missing_billing_info ||
      requisition.is_missing_due_date ||
      requisition.is_missing_treatment_notes ||
      false,
    unread: useCfcResultInAneuploidyColumn
      ? getRowUnread(
          requisition.unity_report?.status || "Not Ordered",
          requisition.cfc_report.status,
          requisition.unity_report?.is_read ?? true,
          requisition.cfc_report.is_read ?? true,
        )
      : (requisition.unity_report &&
          requisition.aneuploidy_report &&
          getRowUnread(
            requisition.unity_report.status,
            requisition.aneuploidy_report.status,
            requisition.unity_report.is_read,
            requisition.aneuploidy_report.is_read,
          )) ||
        false,
    reportSummary: requisition.report_summary || "0 of 2",
    unityCompleteId: requisition.unity_complete_id || undefined,
    cfcEligibleCollectByDate:
      requisition.aneuploidy_report?.cfc_eligible_collect_by_date ?? null,
    cfcEligibleConditions:
      requisition.aneuploidy_report?.cfc_eligible_conditions ?? [],
    aneuploidyReportDate: requisition.aneuploidy_report?.report_date ?? null,
    isCfcEligibilityOverridden: requisition.is_cfc_eligibility_overridden,
    isCfcAlertDismissed: requisition.is_cfc_alert_dismissed ?? false,
    cfcStatus: requisition.cfc_report?.status,
    cfcReportID: requisition.cfc_report?.report_pk,
    archivedAt: requisition.archived_at || undefined,
    archivedByName: requisition.archived_by_name || undefined,
    canRequestReportUpdate: requisition.can_request_report_update,
    reportUpdate: requisition.report_update,
  };
};

interface DetailedRequsitionReportResults {
  sfID: string | undefined;
  reqID: string | undefined;
  name: string;
  carrier: string;
  aneuploidy: string;
  RhD: string;
  fetalSex: string;
  gestation: string;
  eggDonor: string;
}

export const convertDetailedRequisitionToReportResults = (
  requisition: DetailedRequisitionSerializer,
): DetailedRequsitionReportResults => ({
  sfID: requisition.sfid,
  reqID: requisition.req_identifier,
  name: `${requisition.first_name} ${requisition.last_name}`,
  carrier: requisition.unity_report
    ? getReportResultLabel(
        requisition.unity_report.status,
        requisition.unity_report.summary,
      )
    : "Not Ordered",
  aneuploidy: requisition.aneuploidy_report
    ? getReportResultLabel(
        requisition.aneuploidy_report.status,
        requisition.aneuploidy_report.summary,
      )
    : "Not Ordered",
  RhD: requisition.aneuploidy_report
    ? requisition.aneuploidy_report.fetal_rhd_fa
    : "Not Ordered",
  fetalSex: getRowFetalSex(requisition.aneuploidy_report?.fetal_sex),
  gestation: getGestation(requisition.gestation),
  eggDonor: getEggDonor(requisition.egg_donor),
});

export const searchCharacterCountValid = (searchText: string): boolean =>
  searchText.length > 2;

export type ReleaseInfoDisplay =
  | "Release Report"
  | "Patient Notified"
  | "Notification Queued"
  | "Downloaded by Patient"
  | "Viewed by Patient"
  | "Processing"
  | "Not Ordered"
  | "Unable to Contact Patient"
  | "Patient Portal Opted Out"
  | "Releasing";

export const getFinalState = (
  status: PatientReleaseStatus,
): PatientReleaseStatus => {
  if (status === "unable_to_sign_up" || status === "notification_failed") {
    return "unreleased";
  }
  return status;
};

export const getReleaseInfo = (
  releaseStatus: PatientReleaseStatus,
): ReleaseInfoDisplay => {
  switch (getFinalState(releaseStatus)) {
    case "not_pp_enabled":
      return "Patient Portal Opted Out";
    case "not_ordered":
      return "Not Ordered";
    case "releasable":
      return "Release Report";
    case "unreleased":
      return "Unable to Contact Patient";
    case "notified":
    case "released":
      return "Patient Notified";
    case "notification_queued":
      return "Notification Queued";
    case "downloaded":
      return "Downloaded by Patient";
    case "viewed":
      return "Viewed by Patient";
    case "processing":
      return "Processing";
    case "releasing":
      return "Releasing";
    default:
      return "Processing";
  }
};

export const getInitialNewRequisitionForm = (): NewRequisitionForm => ({
  firstName: "",
  middleInitial: "",
  lastName: "",
  medicalRecordNumber: "",
  patientSheetAttached: false,
  sex: "Female",
  maternalWeight: 0,
  dateOfBirth: "",
  estimatedDueDate: "",
  isTwin: false,
  isTripleOrMore: false,
  isEggDonor: false,
  ageOfEggDonor: 0,
  notPregnant: false,
  cellPhone: "",
  emailAddress: "",
  fullAddress: "",
  streetAddress: "",
  aptUnit: "",
  city: "",
  state: "",
  zipCode: "",
  isShipToPatient: true,
  sameAsPatientShippingAddress: true,
  shippingAddressTouched: false,
  shippingFirstName: "",
  shippingLastName: "",
  shippingFullAddress: "",
  shippingStreetAddress: "",
  shippingAptUnit: "",
  shippingCity: "",
  shippingState: "",
  shippingZipCode: "",
  asian: false,
  african: false,
  ashkenazi: false,
  middleEastern: false,
  frenchCanadian: false,
  hispanic: false,
  northernEuropean: false,
  white: false,
  otherEthnicity: false,
  unknownEthnicity: false,
  abnormalUltrasoundFindings: "",
  is22q: false,
  vanishingTwin: false,
  familyHistoryGeneticDisorders: "",
  reportedCarrierPatientAlpha: false,
  reportedCarrierPatientSickle: false,
  reportedCarrierPatientSMA: false,
  reportedCarrierPatientCF: false,
  reportedCarrierPatientVariant: "",
  reportedCarrierFOBAlpha: false,
  reportedCarrierFOBSickle: false,
  reportedCarrierFOBSMA: false,
  reportedCarrierFOBCF: false,
  reportedCarrierFOBVariant: "",
  patientAcknowledgement: false,
  reproductivePartnerFirstName: "",
  reproductivePartnerLastName: "",
  reproductivePartnerDateOfBirth: "",
  paternalReproductivePartner: false,
  maternalReproductivePartner: false,
  carrierScreen: false,
  expandedCarrierPanel: null,
  isRhdOnlyOrder: false,
  aneuploidyNIPT: false,
  aneuploidyTwinNIPT: false,
  aneuploidyOptOutFetalSex: false,
  aneuploidyIncludeRhd: false,
  isFragileX: false,
  isAcogPlus: false,
  isFetalAntigen: false,
  isFetalAntigenRbc: false,
  isFetalAntigenPlatelet: false,
  isFetalAntigenBigC: false,
  isFetalAntigenLittleC: false,
  isFetalAntigenD: false,
  isFetalAntigenDuffy: false,
  isFetalAntigenE: false,
  isFetalAntigenKell: false,
  isFetalAntigenLittleE: false,
  isFetalAntigenFyb: false,
  isFetalAntigenJka: false,
  isFetalAntigenJkb: false,
  isFetalAntigenLittleK: false,
  isFetalAntigenM: false,
  isFetalAntigenN: false,
  isFetalAntigenBigS: false,
  isFetalAntigenLittleS: false,
  isFetalAntigenU: false,
  isFetalAntigenHpa1a: false,
  isFetalAntigenHpa1b: false,
  isFetalAntigenHpa2a: false,
  isFetalAntigenHpa2b: false,
  isFetalAntigenHpa3a: false,
  isFetalAntigenHpa3b: false,
  isFetalAntigenHpa4a: false,
  isFetalAntigenHpa4b: false,
  isFetalAntigenHpa5a: false,
  isFetalAntigenHpa5b: false,
  isFetalAntigenHpa9a: false,
  isFetalAntigenHpa9b: false,
  isFetalAntigenHpa15a: false,
  isFetalAntigenHpa15b: false,
  insuranceSheetAttached: false,
  Z8481: false,
  Z31430: false,
  Z31440: false,
  Z810: false,
  O285_carrier: false,
  Z3481_carrier: false,
  Z3482_carrier: false,
  Z3483_carrier: false,
  O283: false,
  O09511: false,
  O09512: false,
  O09513: false,
  O09521: false,
  O09522: false,
  O09523: false,
  O09891: false,
  O09892: false,
  O09893: false,
  O285_aneuploidy: false,
  Z3481_aneuploidy: false,
  Z3482_aneuploidy: false,
  Z3483_aneuploidy: false,
  cfDNAOptOut: false,
  Z3401: false,
  Z3402: false,
  Z3403: false,
  Z360: false,
  O360110: false,
  O360120: false,
  O360130: false,
  Z3182: false,
  O361910: false,
  O361920: false,
  O361930: false,
  otherICDCodes: "",
  otherCarrierScreenICDCodes: "",
  otherAneuploidyICDCodes: "",
  otherRhdICDCodes: "",
  otherFetalAntigenICDCodes: "",
  orderingHealthcareProviders: "",
  orderingHealthcareProviderFormatted: "",
  orderingHealthcareNPI: "",
  isStandardAccount: true,
  isInternationalAccount: true,
  isMobilePhlebotomyAccount: false,
  isPatientSheetAccount: false,
  isInsuranceSheetAccount: false,
  isFetalAntigenAccount: false,
  isFragileXAccount: false,
  isRhdOnlyAccount: false,
  is130PanelAccount: false,
  is611PanelAccount: false,
  is113CustomPanelAccount: false,
  isMobilePhlebotomy: false,
  providerAuthorization: false,
  clinicName: "",
  clinicPhone: "",
  clinicFax: "",
  clinicAccountNumber: "",
  clinicAdditionalNotes: "",
  billToInsurance: false,
  billToInsuranceType: "Entry",
  insuranceCompanyName: "",
  groupId: "",
  memberId: "",
  policyOwnerFirstName: "",
  policyOwnerLastName: "",
  policyOwnerDob: "",
  relationshipToPolicyOwnerSelf: false,
  relationshipToPolicyOwnerDependent: false,
  relationshipToPolicyOwnerOther: false,
  billToPatient: false,
  billToPatientEmail: "",
  billToClient: false,
  billToPendingMedicaid: false,
  billToCashPay: false,
  editTreatmentNotes: false,
  treatmentNotes: "",
  insuranceFileUrls: {},
  insuranceFileUrlsSecondaryFront: {},
  insuranceFileUrlsSecondaryBack: {},
  antigenFileUrls: {},
  disableAneuploidy: false,
  requireFetalAntigenFile: true,
});
